import { HttpContext } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { Response } from '../../../../shared/models/api';
import { PaymentMode } from '../../../../shared/models/billing';
import { BiharPppGuestApproval } from '../../../../shared/models/guest/bihar-ppp-guest-approval.model';
import { CreateGuestDto } from '../../../../shared/models/guest/create-guest-crud-dto.model';
import { GuestDetailsInfo } from '../../../../shared/models/guest/guest-details-info.model';
import { RenalReplacementHistory } from '../../../../shared/models/guest/guest-details-info.model';
import { NephrologistData } from '../../../../shared/models/guest/guest-nephrologist.model';
import { API_ENDPOINTS } from '../../../../shared/utils/api-endpoints';
import { ApiService } from '../api.service';
import { GuestDetails, Medication, Prescription } from './../../../../shared/models/guest';
import { CreatePrescription } from './../../../../shared/models/guest/create-prescription.model';
import { GeneralInsurance } from './../../../../shared/models/pre-auth';
import { HANDLED_ERRORS } from './../../../../shared/utils/http-context';

import { Observable } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class GuestService extends ApiService {
  private endpoint = API_ENDPOINTS.GUEST;
  private exportEndpoint = API_ENDPOINTS.EXPORT;

  createNewGuest(guestDetails: CreateGuestDto): Observable<Response<CreateGuestDto>> {
    return this.post<Response<CreateGuestDto>>({
      body: guestDetails,
      operation: 'createNewGuest',
      url: this.endpoint.createGuest(),
    });
  }

  createNewGuestToAzureDB(formData: { guestId: string | number }): Observable<Response<any>> {
    return this.post<Response<any>>({
      body: formData,
      operation: 'createNewGuestToAzureDB',
      url: this.endpoint.createGuestToAzureDB(),
    });
  }

  createGuestPrescription(prescriptionDetails: CreatePrescription, guestId: number) {
    return this.post<Response<any>>({
      body: prescriptionDetails,
      operation: 'createGuestPrescription',
      url: this.endpoint.createPrescription(guestId),
    });
  }

  easyEdit(guestId: number, body: Record<string, any>): Observable<Response<string>> {
    return this.patch<Response<any>>({
      body,
      operation: 'easyEdit',
      url: this.endpoint.easyEdit(guestId),
    });
  }

  fetchApprovalPendingBhavyaPatientList(params: Record<string, any> = {}): Observable<Response<GuestDetails[]>> {
    const context = new HttpContext().set(HANDLED_ERRORS, [404]);

    return this.get<Response<GuestDetails[]>>({
      metadata: {
        ...this.getHttpParams(params),
        context,
      },
      operation: 'fetchApprovalPendingBhavyaPatientList',
      url: this.endpoint.approvalPendingBhavyaPatientList(),
    });
  }

  fetchGuestsDetails(guestId: number, params: Record<string, Object> = {}): Observable<Response<GuestDetailsInfo>> {
    return this.get<Response<GuestDetailsInfo>>({
      metadata: {
        ...this.getHttpParams(params),
      },
      operation: 'fetchGuestsDetails',
      url: this.endpoint.guestDetails(guestId),
    });
  }

  fetchGuestsList(params: Record<string, any> = {}): Observable<Response<GuestDetails[]>> {
    const context = new HttpContext().set(HANDLED_ERRORS, [404]);

    return this.get<Response<GuestDetails[]>>({
      metadata: {
        ...this.getHttpParams(params),
        context,
      },
      operation: 'fetchGuestsList',
      url: this.endpoint.guestsList(),
    });
  }

  fetchPrescriptionList(guestId: string): Observable<Response<Prescription[]>> {
    const context = new HttpContext().set(HANDLED_ERRORS, [404]);

    return this.get<Response<Prescription[]>>({
      metadata: {
        context,
      },
      operation: 'fetchPrescriptionList',
      url: this.endpoint.prescriptionList(guestId),
    });
  }

  downloadFetchGuestsListReport(params: Record<string, any>, handledErrors: number[] = []): Observable<any> {
    const context = new HttpContext().set(HANDLED_ERRORS, handledErrors);

    return this.get<any>({
      operation: 'downloadFetchGuestsListReport',
      url: this.exportEndpoint.guestsList(),
      metadata: {
        ...this.getHttpParams(params),
        context,
      },
    });
  }

  downloadFetchGuestsListReportXLSX(params: Record<string, any>, handledErrors: number[] = []): Observable<any> {
    const context = new HttpContext().set(HANDLED_ERRORS, handledErrors);

    return this.get<any>({
      operation: 'downloadFetchGuestsListReportXLSX',
      url: this.exportEndpoint.guestsList(),
      metadata: {
        ...this.getHttpParams(params),
        context,
        responseType: 'blob',
        observe: 'response',
      },
    });
  }

  downloadFetchApprovalPendingPatientXLSX(params: Record<string, any>, handledErrors: number[] = []): Observable<any> {
    const context = new HttpContext().set(HANDLED_ERRORS, handledErrors);

    return this.get<any>({
      operation: 'downloadFetchGuestsListReportXLSX',
      url: this.exportEndpoint.approvalPendingBhavyaPatientList(),
      metadata: {
        ...this.getHttpParams(params),
        context,
        responseType: 'blob',
        observe: 'response',
      },
    });
  }

  fetchPaymentModeList(params: Record<string, any> = {}): Observable<Response<PaymentMode[]>> {
    return this.get<Response<PaymentMode[]>>({
      metadata: { ...this.getHttpParams(params) },
      operation: 'fetchPaymentModeList',
      url: this.endpoint.paymentModeList(),
    });
  }

  fetchGeneralInsuranceList(): Observable<Response<GeneralInsurance[]>> {
    const context = new HttpContext().set(HANDLED_ERRORS, [404]);

    return this.get<Response<GeneralInsurance[]>>({
      metadata: {
        context,
      },
      operation: 'fetchGeneralInsuranceList',
      url: this.endpoint.listGeneralInsurance(),
    });
  }

  fetchMedicationList(): Observable<Response<Medication[]>> {
    const context = new HttpContext().set(HANDLED_ERRORS, [404]);

    return this.get<Response<Medication[]>>({
      metadata: {
        context,
      },
      operation: 'fetchMedicationList',
      url: this.endpoint.medicationList(),
    });
  }

  fetchNephrologistList(
    centertId: number | string,
    params: Record<string, any> = {}
  ): Observable<Response<NephrologistData[]>> {
    const context = new HttpContext().set(HANDLED_ERRORS, [404]);

    return this.get<Response<NephrologistData[]>>({
      metadata: {
        ...this.getHttpParams(params),
        context,
      },
      operation: 'fetchNephrologistList',
      url: this.endpoint.nephrologistList(centertId),
    });
  }

  deletePrescription(prescriptionId: number): Observable<Response<Record<string, string>>> {
    return this.delete<Response<Record<string, string>>>({
      operation: 'deletePrescription',
      url: this.endpoint.prescriptionDelete(prescriptionId),
    });
  }

  changePrescriptionStatus(
    prescriptionId: number,
    changePrescriptionStatusDetails: Record<string, any>
  ): Observable<Response<any>> {
    return this.patch<Response<any>>({
      body: changePrescriptionStatusDetails,
      operation: 'changePrescriptionStatus',
      url: this.endpoint.prescriptionEdit(prescriptionId),
    });
  }

  biharPppGuestApprovalStatus(guestId: number, guestApprovalDetails: BiharPppGuestApproval): Observable<Response<any>> {
    return this.patch<Response<any>>({
      body: guestApprovalDetails,
      operation: 'biharPppGuestApproval',
      url: this.endpoint.biharPppGuestApprovalStatus(guestId),
    });
  }

  fetchCouponDownload(guestId: number, params: Record<string, Object> = {}): Observable<Response<any>> {
    const context = new HttpContext().set(HANDLED_ERRORS, [404]);

    return this.get<Response<GeneralInsurance[]>>({
      metadata: {
        ...this.getHttpParams(params),
        context,
      },
      operation: 'coupondownload',
      url: this.endpoint.couponDownload(guestId),
    });
  }

  sendMobileOtp(otpDetails: {}): Observable<Response<any>> {
    return this.post<Response<any>>({
      body: otpDetails,
      operation: 'otpsend',
      url: this.endpoint.sendOtp(),
    });
  }

  verifyMobileOtp(otpDetails: {}): Observable<Response<any>> {
    return this.post<Response<any>>({
      body: otpDetails,
      operation: 'otpsend',
      url: this.endpoint.otpVerification(),
    });
  }

  updateGuestDetails(
    guestId: number,
    formData: any,
    params: Record<string, Object>,
    handledErrors: number[] = []
  ): Observable<Response<any>> {
    const context = new HttpContext().set(HANDLED_ERRORS, handledErrors);

    return this.put<Response<any>>({
      metadata: {
        ...this.getHttpParams(params),
        context,
      },
      body: formData,
      operation: 'updateGuestDetails',
      url: this.endpoint.updateGuestDetails(guestId),
    });
  }

  updateRenalReplacementHistory(id: number, formData: any): Observable<Response<any>> {
    return this.put<Response<any>>({
      body: formData,
      operation: 'updateRenalReplacementHistory',
      url: this.endpoint.updateRenalReplacementHistory(id),
    });
  }
  uploadFile(file: any): Observable<Response<any>> {
    const context = new HttpContext().set(HANDLED_ERRORS, [422, 400, 404]);

    return this.post<Response<any>>({
      body: file,
      metadata: { context },
      operation: 'uploadFile',
      url: this.endpoint.uploadFile(),
    });
  }

  updateGuestPrescription(prescriptionId: number, updatedPrescriptionDetails: any): Observable<Response<any>> {
    return this.patch<Response<any>>({
      body: updatedPrescriptionDetails,
      operation: 'updatePrescription',
      url: this.endpoint.prescriptionEdit(prescriptionId),
    });
  }

  updateIncenterAppGuest(guestId: number) {
    return this.post<Response<any>>({
      operation: 'updateIncenterAppGuest',
      metadata: {
        ...this.getHttpParams({ guestId }),
      },
      url: this.endpoint.updateIncenterAppGuest(),
    });
  }

  fetchIrregularReport(params: Record<string, Object> = {}): Observable<Response<any>> {
    const context = new HttpContext().set(HANDLED_ERRORS, [404]);

    return this.get<Response<any[]>>({
      metadata: {
        ...this.getHttpParams(params),
        context,
      },
      operation: 'guestIrregularReport',
      url: this.endpoint.guestIrregularReport(),
    });
  }

  downloadIrregularReportXLSX(params: Record<string, any>, handledErrors: number[] = []): Observable<any> {
    const context = new HttpContext().set(HANDLED_ERRORS, handledErrors);

    return this.get<any>({
      operation: 'downloadIrregularReportXLSX',
      url: this.exportEndpoint.guestIrregularReport(),
      metadata: {
        ...this.getHttpParams(params),
        context,
        responseType: 'blob',
        observe: 'response',
      },
    });
  }

  saveTempGuest(params: Record<string, string>, body: any): Observable<Response<any>> {
    return this.post<Response<any>>({
      body,
      metadata: {
        ...this.getHttpParams(params),
      },
      operation: 'saveTempGuest',
      url: this.endpoint.createTempGuest(),
    });
  }

  updateTempGuest(params: Record<string, string>, body: any, guestId: string): Observable<Response<any>> {
    return this.put<Response<any>>({
      body,
      metadata: {
        ...this.getHttpParams(params),
      },
      operation: 'updateTempGuest',
      url: this.endpoint.updateTempGuest(guestId),
    });
  }

  fetchServiceWiseItemCountList(params: Record<string, any> = {}): Observable<Response<any[]>> {
    const context = new HttpContext().set(HANDLED_ERRORS, [404]);

    return this.get<Response<any[]>>({
      metadata: {
        ...this.getHttpParams(params),
        context,
      },
      operation: 'fetchServiceWiseItemCountList',
      url: this.endpoint.fetchServiceWiseItemCountList(),
    });
  }

  updateGuestWeightInfo(data: any): Observable<Response<any[]>> {
    return this.post<Response<any>>({
      body: data,
      operation: 'upsertGuestWeightEntry',
      url: this.endpoint.updateGuestWeightInfo(),
    });
  }

  getCountryCode(): Observable<Response<any>> {
    return this.get<Response<any>>({
      operation: 'getCountryCode',
      url: this.endpoint.fetchCountryCode(),
    });
  }

  getGuestToken(guestId: string, params: Record<string, any> = {}): Observable<Response<any>> {
    const context = new HttpContext().set(HANDLED_ERRORS, [404]);

    return this.get<Response<any>>({
      metadata: {
        ...this.getHttpParams(params),
        context,
      },
      operation: 'getGuestToken',
      url: this.endpoint.getGuestToken(guestId),
    });
  }

  fetchCenterWiseNephrologistList(
    centertId: number | string,
    params: Record<string, any> = {}
  ): Observable<Response<NephrologistData[]>> {
    const context = new HttpContext().set(HANDLED_ERRORS, [404]);

    return this.get<Response<NephrologistData[]>>({
      metadata: {
        ...this.getHttpParams(params),
        context,
      },
      operation: 'fetchNephrologistList',
      url: this.endpoint.centerWiseNephrologistList(centertId),
    });
  }

  fetchFutureNullifiableItems(guestId: number): Observable<Response<any>> {
    return this.get<Response<any>>({
      operation: 'fetchFutureNullifiableItems',
      url: this.endpoint.futureNullifiableItems(guestId),
    });
  }

  saveRenalFollowUp(guestId: number, payload: any): Observable<Response<any>> {
    return this.post<Response<any>>({
      body: payload,
      operation: 'saveRenalFollowUp',
      url: this.endpoint.saveRenalFollowUp(guestId),
    });
  }

  fetchRenalReplacementHistoryById(id: number): Observable<Response<RenalReplacementHistory>> {
    return this.get<Response<RenalReplacementHistory>>({
      operation: 'fetchRenalReplacementHistoryById',
      url: this.endpoint.renalReplacementHistoryById(id),
    });
  }
}
